import { useMemo, useRef, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getApiVersion } from '@/api/version'
import {
  LayoutDashboard, BarChart3, Globe, Server,
  ChevronLeft, ChevronRight as Expand, ChevronRight, ChevronDown, User2, LogOut,
  History,
  // Icon map — all icons the backend catalogue may reference
  MapPin, Radio, Clock, Route, Terminal,
  Bell, SlidersHorizontal, Tag,
  Truck, List, Cpu, Layers, WalletCards, RefreshCw,
  Globe2, CreditCard, BarChart2, FileText,
  Wrench, Package, Zap, Share2, Settings,
  Users, UserCheck, ShieldCheck, KeyRound,
  Briefcase, CheckSquare, Calendar, Send,
  DollarSign, BookOpen, Map, AlertCircle,
  Heart, Battery, BatteryCharging, Link2, Repeat, Grid3X3,
  Building2, Receipt, Wallet, Car, School,
  PawPrint, Bus, Pentagon, Video,
  // Additional icons from backend menu catalogue
  Droplet, PieChart, Activity, UploadCloud,
  Mail, Download, Pencil, Award,
} from 'lucide-react'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { useTenantStore } from '@/store/tenant'
import { getMenu, type MenuItem } from '@/api/menu'
import type { PermissionMap } from '@/types'
import { cn } from '@/utils/cn'

// ── Icon resolver ──────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ElementType> = {
  'history': History,
  'map-pin': MapPin,
  'radio': Radio,
  'clock': Clock,
  'route': Route,
  'terminal': Terminal,
  'server': Server,
  'bell': Bell,
  'sliders': SlidersHorizontal,
  'tag': Tag,
  'pentagon': Pentagon,
  'truck': Truck,
  'list': List,
  'cpu': Cpu,
  'layers': Layers,
  'sim-card': WalletCards,
  'refresh': RefreshCw,
  'globe': Globe,
  'globe2': Globe2,
  'credit-card': CreditCard,
  'bar-chart': BarChart3,
  'bar-chart-2': BarChart2,
  'file-text': FileText,
  'tool': Wrench,
  'package': Package,
  'zap': Zap,
  'share-2': Share2,
  'settings': Settings,
  'users': Users,
  'user-check': UserCheck,
  'shield': ShieldCheck,
  'shield-check': ShieldCheck,
  'key': KeyRound,
  'briefcase': Briefcase,
  'check-square': CheckSquare,
  'calendar': Calendar,
  'send': Send,
  'dollar-sign': DollarSign,
  'book': BookOpen,
  'map': Map,
  'bus': Bus,
  'alert-circle': AlertCircle,
  'heart': Heart,
  'battery': Battery,
  'battery-charging': BatteryCharging,
  'link': Link2,
  'repeat': Repeat,
  'grid': Grid3X3,
  'building': Building2,
  'receipt': Receipt,
  'wallet': Wallet,
  'car': Car,
  'school': School,
  'paw-print': PawPrint,
  'video':       Video,
  // Additional icons
  'droplet':     Droplet,
  'pie-chart':   PieChart,
  'activity':    Activity,
  'upload-cloud': UploadCloud,
  'box':         Package,
  'mail':        Mail,
  'download':    Download,
  'edit-2':      Pencil,
  'layout':      FileText,
  'award':       Award,
  'trophy':      Award,
  'star':        Award,
}

function resolveIcon(name: string): React.ElementType {
  return ICON_MAP[name] ?? Settings
}

// ── Route map: backend menu key → frontend route ───────────────────────────────

const ROUTE_MAP: Record<string, string> = {
  // Tracking
  'tracking.live':     '/map',
  'tracking.history':  '/history',
  'tracking.trips':    '/trips',
  'tracking.packets':  '/packets',
  // Alerts
  'alerts.list':       '/alerts',
  'alerts.manage':     '/alerts',
  'alerts.types':      '/admin/alert-types',
  'alerts.rules':      '/admin/alert-rules',
  'admin.alert_rules': '/admin/alert-rules',
  // Geofences
  'geofences.list':            '/geofences',
  'geofences.events':          '/geofences',
  'geofences.assign_vehicles': '/geofences',
  // Vehicles
  'vehicles.list':          '/vehicles',
  'vehicles.assign_device': '/vehicles',
  'vehicles.types':         '/admin/vehicle-types',
  // Drivers
  'drivers.list':           '/drivers',
  'drivers.assign_vehicle': '/drivers',
  // Devices
  'devices.list':  '/devices',
  'devices.types': '/devices',
  // SIMs
  'sims.list':      '/sims',
  'sims.sync':      '/sims',
  'sims.providers': '/sims',
  // Billing — admin / domain-admin keys
  'billing.dashboard':           '/billing/dashboard',
  'billing.platform_dashboard':  '/billing/dashboard',
  // Domain-admin subscription management (SubscriptionsPage handles owner → MySubscriptionPage internally)
  'billing.overview':            '/billing/subscriptions',
  'billing.subscriptions':       '/billing/subscriptions',
  'billing.manage_subscription': '/billing/subscriptions',
  'billing.entities':            '/billing/subscriptions',
  'billing.manage_entities':     '/billing/subscriptions',
  // Owner self-service subscription (distinct route so dedup keeps it when both groups appear)
  'billing.subscription':        '/billing/my-subscription',
  // Billing profile
  'billing.profiles':            '/billing/profile',
  'billing.profile':             '/billing/profile',
  'billing.manage_profiles':     '/billing/profile',
  // Invoices
  'billing.invoices':            '/billing/invoices',
  'billing.manage_invoices':     '/billing/invoices',
  // Payments & Wallet (admin view — PaymentsWalletPage auto-switches per user type)
  'billing.payments':            '/billing/payments',
  'billing.manage_payments':     '/billing/payments',
  'billing.wallet':              '/billing/payments',
  'billing.manage_wallet':       '/billing/payments',
  'billing.statement':           '/billing/payments',
  // Owner self-service billing (distinct route)
  'billing.my_billing':          '/billing/my-billing',
  // Customers
  'billing.manage_customers':    '/billing/customers',
  // Plans
  'billing.plans':               '/billing/domain-plans',
  'billing.domain_plans':        '/billing/domain-plans',
  'billing.platform_plans':      '/billing/plans',
  'billing.manage_domain_plans': '/billing/domain-plans',
  'billing.platform_plan':       '/billing/platform-plan',
  // Gateways
  'billing.gateways':            '/billing/gateways',
  'billing.manage_gateways':     '/billing/gateways',
  // Technician — dedicated routes (pages TBD; currently stub)
  'technician.stock':    '/technician/stock',
  'technician.activate': '/technician/activate',
  'technician.handover': '/technician/handover',
  // Field Force
  'fieldforce.list':       '/field-force/employees',
  'fieldforce.tasks':      '/field-force/tasks',
  'fieldforce.attendance': '/field-force/attendance',
  // Dispatch
  'dispatch.list':     '/dispatch/bookings',
  'dispatch.services': '/dispatch/service-types',
  'dispatch.finance':  '/dispatch/bookings',
  'dispatch.packages': '/dispatch/packages',
  // School
  'school.list':       '/school/schools',
  'school.students':   '/school/students',
  'school.attendance': '/school/attendance',
  'school.routes':     '/school/routes',
  // Transport
  'transport.list':      '/transport/routes',
  'transport.roster':    '/transport/rosters',
  'transport.sos':       '/transport/sos',
  'transport.vendors':   '/transport/vendors',
  'transport.employees': '/transport/employees',
  // Pets
  'pets.list':   '/pets',
  'pets.breeds': '/pets',
  // Batteries / EV
  'batteries.list':   '/ev/batteries',
  'batteries.assign': '/ev/batteries',
  'batteries.swap':   '/ev/swaps',
  // EV Stations (legacy key)
  'ev_stations.manage': '/ev/stations',
  // EV group (catalogue v2 — new keys from issue #57)
  'ev.status':            '/ev/status',
  'ev.history':           '/ev/history',
  'ev.charging_sessions': '/ev/charging-sessions',
  'ev.stations':          '/ev/stations',
  // Reports (many possible backend keys)
  'reports.list':           '/reports',
  'reports.view':           '/reports',
  'reports.all':            '/reports',
  'reports.generate':       '/reports',
  'reports.trips':          '/reports',
  'reports.trip':           '/reports',
  'reports.speed':          '/reports',
  'reports.distance':       '/reports',
  'reports.mileage':        '/reports',
  'reports.ignition':       '/reports',
  'reports.geofence':       '/reports',
  'reports.geofence_events': '/reports',
  'reports.alerts':         '/reports',
  'reports.alert':          '/reports',
  'reports.summary':        '/reports',
  'reports.manage':         '/reports',

  // Scoring
  'scoring.leaderboard': '/scoring',
  'scoring.config':      '/scoring',
  'scoring.view':        '/scoring',
  'scoring.list':        '/scoring',

  // System (super admin)
  'system.services': '/system',
  'system.parsers':  '/system/parsers',

  // Dashcam — live/recordings/alarm-clips pages not yet implemented;
  'dashcam.live':        '/dashcam',
  'dashcam.recordings':  '/dashcam',
  'dashcam.alarm_clips': '/dashcam',
  'dashcam.config':      '/admin/dashcam-config',

  // My Billing (owner self-service)
  'my_billing.subscription': '/billing/my-subscription',
  'my_billing.invoices':     '/billing/my-billing',
  'my_billing.payments':     '/billing/my-billing',

  // Admin
  'admin.users':             '/admin/users',
  'admin.manage_users':      '/admin/users',
  'admin.user_list':         '/admin/users',
  'admin.list_users':        '/admin/users',
  'admin.user_types':        '/admin/user-types',
  'admin.permissions':       '/admin/user-types',
  'admin.manage_user_types': '/admin/user-types',
  'admin.data_brokers':      '/admin/data-brokers',
  'admin.api_keys':          '/admin/api-keys',
  'admin.domains':           '/white-label/domains',
  'admin.dashcam_config':    '/admin/dashcam-config',
  'admin.doc_reminders':     '/admin/document-reminders',
  // Settings keys
  'settings.api_keys':       '/admin/api-keys',
  // Alert Rules — extra key formats the backend may send
  'alert_rules':            '/admin/alert-rules',
  'alert_rule':             '/admin/alert-rules',
  // Calendars
  'calendars':              '/admin/calendars',
  'admin.calendars':        '/admin/calendars',
  'calendar':               '/admin/calendars',
  // Billing extras
  'billing.customers':           '/billing/customers',
  'billing.pdf_templates':       '/billing/pdf-templates',
  'billing.pdf-templates':       '/billing/pdf-templates',
  'billing.tax_config':          '/billing/tax-config',
  'billing.signing_cert':        '/billing/signing-cert',
  'billing.dunning_rules':       '/billing/dunning-rules',
  'billing.platform_profile':        '/billing/platform-profile',
  'billing.platform_gateways':       '/billing/platform-gateways',
  'billing.platform_pdf_templates':  '/billing/platform-pdf-templates',
  // Billing exports — backend menu keys from /menu API
  'billing.exports':             '/billing/tax-config',
  // Integration & settings keys from /menu API
  'integrations.databroker':     '/admin/data-brokers',
  'settings.domain':             '/admin/domain',
  'settings.notifications':      '/admin/notifications',
  // Calendars group (now its own top-level group in catalogue)
  'calendars.list':              '/admin/calendars',
  'calendars.manage':            '/admin/calendars',
  // billing.platform_plans — plural is the canonical key in catalogue v2
  // billing.platform_plan (singular) kept above as legacy alias
}

// ── Label overrides — force specific display labels regardless of API value ────
const LABEL_OVERRIDE: Record<string, string> = {
  'billing.dashboard':           'Billing Dashboard',
  'billing.platform_dashboard':  'Billing Dashboard',
  // Owner self-service keys → owner labels
  'billing.subscription':        'My Subscription',
  'billing.my_billing':          'My Billing',
  // Admin billing keys → proper admin labels
  'billing.overview':            'Subscriptions',
  'billing.subscriptions':       'Subscriptions',
  'billing.manage_subscription': 'Subscriptions',
  'billing.entities':            'Subscriptions',
  'billing.manage_entities':     'Subscriptions',
  'billing.payments':            'Payments',
  'billing.manage_payments':     'Payments',
  'billing.wallet':              'Payments',
  'billing.manage_wallet':       'Payments',
  'billing.statement':           'Payments',
  // My Billing group (owner self-service)
  'my_billing.subscription': 'Subscription',
  'my_billing.invoices':     'Invoices',
  'my_billing.payments':     'Payments',
  'tracking.live':         'Live',
  'tracking.history':      'GPS History',
  'tracking.trips':        'Trips',
  // Dashcam
  'dashcam.live':        'Live View',
  'dashcam.recordings':  'Recordings',
  'dashcam.alarm_clips': 'Alarm Clips',
  'dashcam.config':      'Config',
  'alert_rules':           'Alert Rules',
  'alert_rule':            'Alert Rules',
  'alerts.rules':          'Alert Rules',
  'calendars.list':        'Calendars',
  'admin.domains':         'White Label',
  'billing.customers':         'Customer Billing',
  'billing.manage_customers':  'Customer Billing',
  'billing.plans':                    'Plans',
  'billing.domain_plans':             'Plans',
  'billing.platform_plans':           'Platform Plans',
  'billing.platform_pdf_templates':   'Platform PDF Templates',
  // All reports.* keys that link to /reports → rename to "On-Road Summary"
  'reports.list':     'On-Road Summary',
  'reports.view':     'On-Road Summary',
  'reports.all':      'On-Road Summary',
  'reports.generate': 'On-Road Summary',
  'reports.trips':    'On-Road Summary',
  'reports.trip':     'On-Road Summary',
  'reports.summary':  'On-Road Summary',
  'reports.manage':   'On-Road Summary',
  'reports.mileage':  'On-Road Summary',
  'reports.distance': 'On-Road Summary',
  'reports.speed':    'On-Road Summary',
  'reports.ignition': 'On-Road Summary',
  'reports.geofence': 'On-Road Summary',
  'reports.alerts':   'On-Road Summary',
  'reports.alert':    'On-Road Summary',
}

// ── Post-process menu from /menu API ──────────────────────────────────────────
// The API returns a fully-filtered tree per user — no client-side permission
// filtering needed. This function:
//   1. Applies display label overrides (UX labels differ from API labels)
//   2. Moves tracking.history + tracking.trips into the Reports group
//   3. Strips super-admin-only billing keys from non-super users (safety guard)
//   4. Strips payment/wallet/statement keys from billing group (shown as tabs in Customer Billing)
//   5. Cross-group route deduplication — the first group to claim a route wins;
//      later groups lose items that map to already-claimed routes.
//      This prevents the same page appearing under both MY BILLING and BILLING groups.
function postProcessMenu(groups: MenuItem[], isSuperAdmin = false): MenuItem[] {
  const MOVE_TO_REPORTS = new Set(['tracking.history', 'tracking.trips'])

  // Collect items to promote to Reports
  const promotedItems: MenuItem[] = []
  groups.forEach((g) => {
    if (g.key === 'tracking') {
      ;(g.children ?? []).forEach((c) => {
        if (MOVE_TO_REPORTS.has(c.key)) promotedItems.push(c)
      })
    }
  })

  const applyOverride = (c: MenuItem): MenuItem => ({
    ...c,
    label: LABEL_OVERRIDE[c.key] ?? c.label,
  })

  // Pass 1: label overrides + tracking→reports promotion
  const pass1 = groups.map((group) => {
    if (group.key === 'tracking') {
      return {
        ...group,
        children: (group.children ?? [])
          .filter((c) => !MOVE_TO_REPORTS.has(c.key))
          .map(applyOverride),
      }
    }

    if (group.key === 'reports') {
      return {
        ...group,
        children: [
          ...promotedItems.map(applyOverride),
          ...(group.children ?? []).map((c) => {
            // Any child routing to /reports → rename to "On-Road Summary"
            if (ROUTE_MAP[c.key] === '/reports') return { ...c, label: 'On-Road Summary' }
            return applyOverride(c)
          }),
        ],
      }
    }

    return {
      ...group,
      children: (group.children ?? []).map(applyOverride),
    }
  })

  // Pass 2: strip billing.payments / billing.wallet / billing.statement from the top-level
  // billing group. These pages are embedded as tabs inside Customer Billing (/billing/customers)
  // so a standalone nav item is redundant and confusing.
  // Also strip super-admin-only platform billing keys from non-super users as a safety guard
  // (the API should already filter these, but we guard in case of backend config drift).
  const PAYMENTS_KEYS_IN_CUSTOMER_TAB = new Set([
    'billing.payments', 'billing.manage_payments', 'billing.wallet', 'billing.manage_wallet', 'billing.statement',
  ])
  // Keys that only super admin should ever see in the billing menu
  const SUPER_ADMIN_ONLY_BILLING_KEYS = new Set([
    'billing.platform_plans',
    'billing.platform_gateways',
    'billing.platform_profile',
    'billing.platform_dashboard',
    // billing.platform_pdf_templates is intentionally NOT here —
    // domain admins also need Platform PDF Templates
  ])
  const pass2 = pass1.map((group) => {
    if (group.key !== 'billing') return group
    return {
      ...group,
      children: (group.children ?? []).filter((c) => {
        if (PAYMENTS_KEYS_IN_CUSTOMER_TAB.has(c.key)) return false
        if (!isSuperAdmin && SUPER_ADMIN_ONLY_BILLING_KEYS.has(c.key)) return false
        return true
      }),
    }
  })

  // Pass 3: suppress my_billing group when the user also has the full admin billing group.
  // billing:view is shared between owner self-service and admin billing, so the backend
  // returns both groups for any user with that permission. Admin users should only see
  // the full BILLING admin group — MY BILLING (owner self-service) is for customers only.
  const hasBillingAdminGroup = pass2.some(
    (g) => g.key === 'billing' && (g.children ?? []).length > 0
  )
  const pass3 = hasBillingAdminGroup
    ? pass2.filter((g) => g.key !== 'my_billing')
    : pass2

  // Pass 4: cross-group route deduplication
  // Routes claimed by an earlier group are stripped from later groups.
  // This prevents duplicate nav items when the API returns both my_billing.*
  // and billing.* keys that resolve to the same frontend route.
  const claimedRoutes = new Set<string>()
  return pass3.map((group) => ({
    ...group,
    children: (group.children ?? []).filter((child) => {
      const route = ROUTE_MAP[child.key]
      if (!route) return true            // unknown key — keep (MenuGroup handles it)
      if (claimedRoutes.has(route)) return false
      claimedRoutes.add(route)
      return true
    }),
  }))
}

// ── Group-level route fallback ─────────────────────────────────────────────────
// If a child key from the backend isn't in ROUTE_MAP, use the group's base route.
// This prevents entire menu groups from disappearing due to unknown child keys.
const GROUP_ROUTE_FALLBACK: Record<string, string> = {
  reports:    '/reports',
  tracking:   '/map',
  alerts:     '/alerts',
  geofences:  '/geofences',
  vehicles:   '/vehicles',
  drivers:    '/drivers',
  devices:    '/devices',
  sims:       '/sims',
  dashcam:      '/dashcam',
  technician:   '/technician/stock',
  my_billing:   '/billing/my-billing',
  billing:      '/billing/subscriptions',
  integrations: '/admin/data-brokers',
  settings:     '/admin/domain',
  calendars:    '/admin/calendars',
  fieldforce:   '/field-force/employees',
  school:     '/school/schools',
  transport:  '/transport/routes',
  pets:       '/pets',
  batteries:  '/ev/batteries',
  ev:         '/ev/status',
  dispatch:   '/dispatch/bookings',
  // admin group has no single fallback route — each item must be in ROUTE_MAP
  system:     '/system',
}

// ── Nav item component ─────────────────────────────────────────────────────────

function SidebarItem({
  to, label, icon: Icon, collapsed,
}: {
  to: string; label: string; icon: React.ElementType; collapsed: boolean
}) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors',
          isActive
            ? 'bg-primary/10 text-primary font-bold'
            : 'text-sidebar-fg font-medium hover:bg-sidebar-hover hover:text-sidebar-active-fg'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  )
}

// ── Menu group renderer ────────────────────────────────────────────────────────

interface InjectedItem { afterKey: string; to: string; label: string; icon: React.ElementType }

function MenuGroup({
  group, collapsed, inject = [], isOpen, onToggle,
}: {
  group: MenuItem
  collapsed: boolean
  inject?: InjectedItem[]
  isOpen: boolean
  onToggle: () => void
}) {
  const groupRef = useRef<HTMLDivElement>(null)

  // Scroll group into view when it opens so sub-items aren't hidden below the fold
  useEffect(() => {
    if (isOpen && groupRef.current) {
      // Small delay to let the DOM expand first
      const t = setTimeout(() => {
        groupRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }, 60)
      return () => clearTimeout(t)
    }
  }, [isOpen])

  const children = group.children ?? []
  if (children.length === 0) return null

  // De-duplicate children by route; fall back to group-level route for unknown keys
  const seen = new Set<string>()
  const unique = children.filter((child) => {
    const route = ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key]
    if (!route || seen.has(route)) return false
    seen.add(route)
    return true
  })
  if (unique.length === 0) return null

  // Build final list, inserting injected items after their anchor key
  const items: { key: string; to: string; label: string; icon: React.ElementType }[] = []
  for (const child of unique) {
    items.push({ key: child.key, to: ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key] ?? '/', label: child.label, icon: resolveIcon(child.icon) })
    for (const inj of inject) {
      if (inj.afterKey === child.key && !items.some((i) => i.to === inj.to)) {
        items.push({ key: `__inject_${inj.to}`, to: inj.to, label: inj.label, icon: inj.icon })
      }
    }
  }

  // Icon-only (collapsed sidebar) — show divider + items, no accordion header
  if (collapsed) {
    return (
      <>
        <div className="my-1.5 border-t border-border" />
        {items.map((item) => (
          <SidebarItem key={item.key} to={item.to} label={item.label} icon={item.icon} collapsed={collapsed} />
        ))}
      </>
    )
  }

  // Full sidebar — accordion group
  const GroupIcon = resolveIcon(group.icon ?? '')
  return (
    <div ref={groupRef} className="mt-1">
      <button
        onClick={onToggle}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2 transition-colors',
          isOpen
            ? 'text-sidebar-active-fg'
            : 'text-muted hover:text-sidebar-active-fg hover:bg-sidebar-hover'
        )}
      >
        <span className="flex items-center gap-2">
          <GroupIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wider">{group.label}</span>
        </span>
        {isOpen
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        }
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5 pl-4">
          {items.map((item) => (
            <SidebarItem key={item.key} to={item.to} label={item.label} icon={item.icon} collapsed={collapsed} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Fallback menu builder (used when /menu returns empty — backend bug) ────────

function buildFallbackMenu(perms: PermissionMap, isSuperAdmin: boolean): MenuItem[] {
  const can = (r: string) => isSuperAdmin || perms[r]?.canRead === true
  const item = (key: string, label: string, icon: string): MenuItem => ({ key, label, icon })
  const group = (key: string, label: string, icon: string, children: MenuItem[]): MenuItem =>
    ({ key, label, icon, children })

  const groups: MenuItem[] = []

  // Tracking — only Live map stays here
  const trackingItems: MenuItem[] = []
  if (can('tracking') || can('vehicles')) trackingItems.push(item('tracking.live',    'Live',              'map-pin'))
  if (can('devices') || can('tracking'))  trackingItems.push(item('tracking.packets', 'Packet Inspector',  'terminal'))
  if (trackingItems.length) groups.push(group('tracking', 'Tracking', 'map-pin', trackingItems))

  // Dashcam — after Tracking
  if (can('dashcam')) {
    const dashcamItems: MenuItem[] = []
    dashcamItems.push(item('dashcam.live',        'Live View',   'video'))
    dashcamItems.push(item('dashcam.recordings',  'Recordings',  'download'))
    dashcamItems.push(item('dashcam.alarm_clips', 'Alarm Clips', 'bell'))
    dashcamItems.push(item('dashcam.config',      'Config',      'settings'))
    groups.push(group('dashcam', 'Dashcam', 'video', dashcamItems))
  }

  // Alerts
  if (can('alerts'))    groups.push(group('alerts',    'Alerts',    'bell',    [item('alerts.list',    'Alerts',    'bell')]))
  // Geofences
  if (can('geofences')) groups.push(group('geofences', 'Geofences', 'pentagon', [item('geofences.list', 'Geofences', 'pentagon')]))
  // Vehicles
  if (can('vehicles'))  groups.push(group('vehicles',  'Vehicles',  'truck',   [item('vehicles.list', 'Vehicles', 'truck'), item('vehicles.types', 'Vehicle Types', 'layers')]))
  // Drivers
  if (can('drivers'))   groups.push(group('drivers',   'Drivers',   'user-check', [item('drivers.list', 'Drivers', 'user-check')]))
  // Devices
  if (can('devices'))   groups.push(group('devices',   'Devices',   'cpu',    [item('devices.list', 'Devices', 'cpu')]))
  // SIMs
  if (can('sims'))      groups.push(group('sims',      'SIM Cards', 'sim-card', [item('sims.list', 'SIM Cards', 'sim-card')]))

  // My Billing — owner self-service, only for users WITHOUT full admin billing access.
  // billing:view is shared between owner and admin, so we must explicitly exclude
  // users who also have admin billing features (they get the full BILLING group below).
  const hasAdminBilling = can('billing') || can('billing_subscriptions') || can('billing_plans') || can('billing_invoices')
  if (!isSuperAdmin && !hasAdminBilling && can('my_billing')) {
    groups.push(group('my_billing', 'My Billing', 'credit-card', [
      item('my_billing.subscription', 'Subscription', 'receipt'),
      item('my_billing.invoices',     'Invoices',     'file-text'),
      item('my_billing.payments',     'Payments',     'credit-card'),
    ]))
  }

  // Billing
  if (hasAdminBilling) {
    const billingItems: MenuItem[] = []
    // Dashboard — first item for all billing users
    billingItems.push(item('billing.dashboard',     'Billing Dashboard', 'bar-chart-2'))
    // Domain admin features
    if (!isSuperAdmin) {
      billingItems.push(item('billing.subscriptions',        'Subscriptions',         'receipt'))
      billingItems.push(item('billing.customers',            'Customer Billing',       'users'))
      billingItems.push(item('billing.invoices',             'Invoices',               'file-text'))
      // billing.payments is accessible via the Payments tab inside Customer Billing — no standalone nav item
      billingItems.push(item('billing.domain_plans',         'Domain Plans',           'layers'))
      billingItems.push(item('billing.platform_plan',        'Platform Plan',          'package'))
      billingItems.push(item('billing.gateways',             'Payment Gateways',       'credit-card'))
      billingItems.push(item('billing.platform_pdf_templates', 'Platform PDF Templates', 'file-text'))
    }
    // Super admin
    if (isSuperAdmin) {
      billingItems.push(item('billing.invoices',           'Invoices',           'file-text'))
      billingItems.push(item('billing.platform_gateways',  'Payment Gateways',   'credit-card'))
    }
    billingItems.push(item('billing.profiles', 'Profile', 'building'))
    groups.push(group('billing', 'Billing', 'credit-card', billingItems))
  }

  // Technician
  if (can('technician')) {
    groups.push(group('technician', 'Technician', 'tool', [
      item('technician.stock',    'Stock',    'package'),
      item('technician.activate', 'Activate', 'zap'),
      item('technician.handover', 'Handover', 'share-2'),
    ]))
  }

  // Field Force
  if (can('fieldforce')) {
    groups.push(group('fieldforce', 'Field Force', 'briefcase', [
      item('fieldforce.list',       'Employees',  'users'),
      item('fieldforce.tasks',      'Tasks',      'check-square'),
      item('fieldforce.attendance', 'Attendance', 'calendar'),
    ]))
  }

  // School
  if (can('school')) {
    groups.push(group('school', 'School Bus', 'school', [
      item('school.list',       'Schools',    'school'),
      item('school.routes',     'Routes',     'route'),
      item('school.students',   'Students',   'users'),
      item('school.attendance', 'Attendance', 'calendar'),
    ]))
  }

  // Transport
  if (can('transport')) {
    groups.push(group('transport', 'Transport', 'bus', [
      item('transport.vendors',   'Vendors',   'building'),
      item('transport.employees', 'Employees', 'users'),
      item('transport.list',      'Routes',    'route'),
      item('transport.roster',    'Rosters',   'list'),
      item('transport.sos',       'SOS',       'bell'),
    ]))
  }

  // Pets
  if (can('pets')) groups.push(group('pets', 'Pets', 'paw-print', [item('pets.list', 'Pets', 'paw-print')]))

  // EV (new catalogue v2 keys from issue #57)
  if (can('ev') || can('batteries')) {
    const evItems: MenuItem[] = []
    const canEvStations = isSuperAdmin || perms['ev']?.canCreate === true
    if (can('ev'))        { evItems.push(item('ev.status',            'EV Status',          'battery')) }
    if (can('ev'))        { evItems.push(item('ev.history',           'EV History',         'activity')) }
    if (can('ev'))        { evItems.push(item('ev.charging_sessions', 'Charging Sessions',  'zap')) }
    if (canEvStations)    { evItems.push(item('ev.stations',          'EV Stations',        'zap')) }
    // Legacy batteries (kept for deployments still on old catalogue)
    if (can('batteries')) { evItems.push(item('batteries.list', 'Batteries', 'battery')); evItems.push(item('batteries.swap', 'Swaps', 'repeat')) }
    if (evItems.length)   groups.push(group('ev', 'EV', 'battery-charging', evItems))
  }

  // Dispatch
  if (can('dispatch')) {
    groups.push(group('dispatch', 'Dispatch', 'send', [
      item('dispatch.list',     'Bookings',      'send'),
      item('dispatch.services', 'Service Types', 'settings'),
      item('dispatch.packages', 'Packages',      'package'),
    ]))
  }

  // Reports — GPS History + Trips + On-Road Summary
  const reportItems: MenuItem[] = []
  if (can('tracking') || can('vehicles'))  reportItems.push(item('tracking.history', 'GPS History',     'clock'))
  if (can('trips')    || can('tracking'))  reportItems.push(item('tracking.trips',   'Trips',           'route'))
  if (can('reports')  || can('report') || can('reporting'))
                                           reportItems.push(item('reports.list',     'On-Road Summary',  'bar-chart'))
  if (reportItems.length) groups.push(group('reports', 'Reports', 'bar-chart', reportItems))

  // Administration
  const adminItems: MenuItem[] = []
  if (can('users')              || isSuperAdmin) adminItems.push(item('admin.users',        'Users',        'users'))
  if (can('user_types')         || isSuperAdmin) adminItems.push(item('admin.user_types',   'User Types',   'shield'))
  if (can('databroker_configs') || isSuperAdmin) adminItems.push(item('admin.data_brokers', 'Data Brokers', 'share-2'))
  if (can('alert_rules') || isSuperAdmin) adminItems.push(item('alerts.rules', 'Alert Rules', 'bell'))
  if (can('calendars')   || isSuperAdmin) adminItems.push(item('admin.calendars',   'Calendars',   'calendar'))
  if (isSuperAdmin) {
    // dashcam.config is already in the dashcam group — only add doc reminders here
    adminItems.push(item('admin.doc_reminders',   'Doc Reminders',        'bell'))
  }
  // API Keys: requires api_keys:manage permission (previously was super-admin only)
  if (isSuperAdmin || can('api_keys')) adminItems.push(item('admin.api_keys', 'API Keys', 'key'))
  if (adminItems.length) groups.push(group('admin', 'Administration', 'settings', adminItems))

  // Billing extras — visible to domain admins with billing perm (not just super-admin)
  // Tax Config, PDF Templates, Signing Cert, and Dunning Rules are domain-scoped features
  if (isSuperAdmin || can('billing') || can('billing.tax_config') || can('billing.invoices')) {
    const billingGroup = groups.find((g) => g.key === 'billing')
    if (billingGroup) {
      billingGroup.children = billingGroup.children ?? []
      billingGroup.children.push(item('billing.tax_config',    'Tax Config',        'shield'))
      billingGroup.children.push(item('billing.pdf_templates', 'PDF Templates',     'file-text'))
      billingGroup.children.push(item('billing.signing_cert',  'Signing Cert',      'award'))
      billingGroup.children.push(item('billing.exports',       'GST Exports',       'download'))
      billingGroup.children.push(item('billing.dunning_rules', 'Payment Reminders', 'bell'))
    }
  }

  // Super-admin-only billing items
  if (isSuperAdmin) {
    const billingGroup = groups.find((g) => g.key === 'billing')
    if (billingGroup) {
      billingGroup.children = billingGroup.children ?? []
      // Insert Platform PDF Templates after Tax Config
      const taxIdx = billingGroup.children.findIndex((c) => c.key === 'billing.tax_config')
      const pdfItem = item('billing.platform_pdf_templates', 'Platform PDF Templates', 'file-text')
      if (taxIdx >= 0) billingGroup.children.splice(taxIdx + 1, 0, pdfItem)
      else billingGroup.children.push(pdfItem)
      billingGroup.children.push(item('billing.platform_profile', 'Platform Profile', 'building'))
    }
  }

  return groups
}

// ── Main Sidebar ───────────────────────────────────────────────────────────────

export function Sidebar() {
  const { t } = useTranslation('nav')
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const theme = useTenantStore((s) => s.theme)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const is_super = useAuthStore((s) => s.is_super)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const permMap = useAuthStore((s) => s.permissions)
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded)
  const { data: apiVersion } = useQuery({
    queryKey: ['api-version'],
    queryFn: getApiVersion,
    staleTime: Infinity,
    retry: false,
  })
  const navRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const [openGroup, setOpenGroup] = useState<string | null>(null)

  const { data: menuGroups = [], isLoading } = useQuery({
    queryKey: ['menu'],
    queryFn: getMenu,
    staleTime: 60_000,
    enabled: isAuthenticated,
  })

  // The /menu API returns a fully-filtered tree for the current user — no
  // client-side rebuilding needed. postProcessMenu only applies label overrides
  // and moves tracking.history/trips into the Reports group (UX convention).
  // If the API returns empty (e.g. first load before permissions propagate),
  // buildFallbackMenu() is used as a safety net.
  const effectiveGroups = useMemo(() => {
    if (menuGroups.length > 0) {
      return postProcessMenu(menuGroups, is_super)
    }
    if (isLoading || !permissionsLoaded) return []
    // Fallback: build from local permission map when API returns empty
    return buildFallbackMenu(permMap, is_super)
  }, [menuGroups, isLoading, permissionsLoaded, permMap, is_super])

  // Auto-open the group whose child route matches the current pathname
  useEffect(() => {
    if (!effectiveGroups.length) return
    for (const group of effectiveGroups) {
      const children = group.children ?? []
      const hasActiveChild = children.some((child) => {
        const route = ROUTE_MAP[child.key] ?? GROUP_ROUTE_FALLBACK[group.key]
        return route && (pathname === route || pathname.startsWith(route + '/'))
      })
      if (hasActiveChild) {
        setOpenGroup(group.key)
        return
      }
    }
  }, [pathname, effectiveGroups])

  // Scroll active nav item into view whenever the route or menu changes
  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const active = nav.querySelector<HTMLElement>('[aria-current="page"]')
    if (active) active.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [pathname, effectiveGroups])

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-30 flex flex-col bg-sidebar border-e border-border transition-all duration-200',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo area */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4">
        {theme?.logoUrl ? (
          <img src={theme.logoUrl} alt={theme.appName} className="h-8 w-auto object-contain" />
        ) : (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-primary text-primary-fg text-xs font-bold">
            {(theme?.appName ?? 'T').slice(0, 2).toUpperCase()}
          </div>
        )}
        {!collapsed && (
          <span className="truncate text-sm font-semibold text-sidebar-active-fg">
            {theme?.appName ?? 'Telematics'}
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="flex-1 space-y-0.5 overflow-y-auto p-2">
        {/* Dashboard — always visible */}
        <SidebarItem
          to="/"
          label={t('dashboard')}
          icon={LayoutDashboard}
          collapsed={collapsed}
        />

        {/* Dynamic menu — fully driven by /menu API + permission-based fallback */}
        {isLoading ? (
          !collapsed && (
            <div className="mt-4 space-y-2 px-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton h-7 rounded" />
              ))}
            </div>
          )
        ) : (
          effectiveGroups.map((group) => (
            <MenuGroup
              key={group.key}
              group={group}
              collapsed={collapsed}
              isOpen={openGroup === group.key}
              onToggle={() => setOpenGroup(prev => prev === group.key ? null : group.key)}
            />
          ))
        )}

        {/* White Label comes from API as admin.domains (gated by domains:manage for super admin) */}
      </nav>

      {/* User footer */}
      <div className="shrink-0 border-t border-border p-2">
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
              isActive
                ? 'bg-primary/10 text-primary font-semibold'
                : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-sidebar-active-fg'
            )
          }
        >
          <User2 className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="flex-1 truncate">{user?.name ?? t('profile')}</span>
          )}
        </NavLink>

        <button
          onClick={() => logout()}
          className="mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-sidebar-fg transition-colors hover:bg-sidebar-hover hover:text-sidebar-active-fg"
          title={collapsed ? t('logout') : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <div className="flex flex-1 items-center justify-between gap-1 min-w-0">
              <span className="truncate">{t('logout')}</span>
              {apiVersion && (
                <span
                  className="shrink-0 text-[9px] text-muted/60 tabular-nums"
                  title={`Build: ${apiVersion.commit} · ${new Date(apiVersion.build_date).toLocaleDateString()}`}
                >
                  ({apiVersion.version.replace(/-unknown$/, '')}
                  {apiVersion.version.endsWith('-unknown') && <span className="text-amber-500 font-semibold"> dev</span>})
                </span>
              )}
            </div>
          )}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="absolute -end-3 top-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-muted shadow-sm hover:text-primary"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <Expand className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>
    </aside>
  )
}
